import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, UserCheck, Heart, Star, Sparkles } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Section, SectionHeader } from '@/components/ui/Section';
import { Modal } from '@/components/ui/Modal';
import { staffConfig } from '@/config/site';
import { cn } from '@/lib/utils';

// Tier gradient colors for supporters
const tierGradients = {
  Mithril: 'from-sky-400 via-blue-500 to-cyan-500',
  Adamantite: 'from-red-900 via-red-600 to-red-400',
  Cobalt: 'from-blue-600 via-blue-500 to-cyan-400',
  Thorium: 'from-emerald-800 via-green-600 to-lime-400',
  Gold: 'from-amber-400 via-yellow-300 to-white',
  Silver: 'from-gray-400 via-slate-300 to-white',
  Iron: 'from-gray-600 via-slate-500 to-gray-400',
  Copper: 'from-orange-600 via-amber-500 to-yellow-300',
};

const tierBorderColors = {
  Mithril: 'border-sky-500',
  Adamantite: 'border-red-600',
  Cobalt: 'border-blue-500',
  Thorium: 'border-green-600',
  Gold: 'border-amber-400',
  Silver: 'border-gray-400',
  Iron: 'border-gray-500',
  Copper: 'border-orange-500',
};

const tierTextColors = {
  Mithril: 'text-sky-400',
  Adamantite: 'text-red-500',
  Cobalt: 'text-blue-500',
  Thorium: 'text-green-500',
  Gold: 'text-amber-400',
  Silver: 'text-gray-400',
  Iron: 'text-gray-500',
  Copper: 'text-orange-500',
};

// Staff card component
function StaffCard({ staff, onSelect, roleColor, isSpecial = false }) {
  // Use custom avatar if provided, unknown avatar for placeholder, otherwise use hyvatar
  const avatarUrl = staff.customAvatar
    ? staff.customAvatar
    : staff.placeholder
    ? staffConfig.unknownAvatar
    : `https://hyvatar.io/render/${staff.username}?size=256`;
  const fallbackUrl = 'https://hyvatar.io/render/NPC?size=256';

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(staff)}
      className={cn(
        'w-full p-6 rounded-xl border bg-card/50 backdrop-blur-sm',
        'hover:shadow-lg transition-all text-left',
        'flex flex-col items-center gap-4',
        staff.placeholder && 'opacity-70',
        isSpecial
          ? 'border-yellow-500/50 hover:border-yellow-400/70 hover:shadow-[0_0_30px_rgba(234,179,8,0.3)]'
          : 'border-border hover:border-primary/30'
      )}
      style={isSpecial ? {
        background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, rgba(251, 191, 36, 0.1) 50%, rgba(234, 179, 8, 0.05) 100%)',
      } : {}}
    >
      <div className="relative">
        <img
          src={avatarUrl}
          alt={staff.username}
          onError={(e) => { e.target.src = fallbackUrl; }}
          className={cn(
            'w-24 h-24 rounded-full object-cover border-2 bg-muted',
            isSpecial ? 'border-yellow-500' : 'border-border'
          )}
          loading="lazy"
        />
        <div
          className={cn(
            'absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center',
            isSpecial ? 'bg-yellow-500' : roleColor
          )}
        >
          {isSpecial ? (
            <Star className="h-3 w-3 text-yellow-900" fill="currentColor" />
          ) : staff.role === 'Owner' ? (
            <UserCheck className="h-3 w-3 text-primary-foreground" />
          ) : (
            <User className="h-3 w-3 text-primary-foreground" />
          )}
        </div>
        
        {/* Gold shimmer effect for special cards */}
        {isSpecial && (
          <div className="absolute inset-0 rounded-full pointer-events-none overflow-hidden">
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-yellow-500/20 to-transparent" />
          </div>
        )}
      </div>

      <div className="text-center">
        <h3 className={cn(
          'font-display font-semibold',
          isSpecial ? 'text-yellow-500' : 'text-foreground'
        )}>
          {staff.username}
        </h3>
        <p className={cn(
          'text-sm font-medium',
          isSpecial ? 'text-yellow-600 dark:text-yellow-400' : roleColor.replace('bg-', 'text-')
        )}>
          {staff.role}
        </p>
        <p className="text-xs text-muted-foreground mt-1">{staff.title}</p>
      </div>
    </motion.button>
  );
}

// Special Thanks card with prisma/rainbow pastel styling
function SpecialThanksCard({ staff, onSelect }) {
  const avatarUrl = staff.placeholder
    ? staffConfig.unknownAvatar
    : `https://hyvatar.io/render/${staff.username}?size=256`;
  const fallbackUrl = 'https://hyvatar.io/render/NPC?size=256';

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      whileHover={{ 
        scale: 1.03,
        boxShadow: '0 0 40px rgba(168, 85, 247, 0.3)',
      }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(staff)}
      className="w-full p-6 rounded-xl border-2 border-transparent hover:border-purple-400/50 
                 transition-all text-left flex flex-col items-center gap-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(251, 207, 232, 0.15) 0%, rgba(196, 181, 253, 0.15) 25%, rgba(165, 243, 252, 0.15) 50%, rgba(187, 247, 208, 0.15) 75%, rgba(254, 240, 138, 0.15) 100%)',
        borderImage: 'linear-gradient(135deg, #fce7f3, #c4b5fd, #a5f3fc, #bbf7d0, #fef08a) 1',
      }}
    >
      {/* Prisma shimmer animation overlay */}
      <motion.div
        className="absolute inset-0 pointer-events-none opacity-30"
        animate={{
          background: [
            'linear-gradient(90deg, transparent 0%, rgba(251, 207, 232, 0.3) 25%, rgba(196, 181, 253, 0.3) 50%, rgba(165, 243, 252, 0.3) 75%, transparent 100%)',
            'linear-gradient(90deg, transparent 0%, rgba(196, 181, 253, 0.3) 25%, rgba(165, 243, 252, 0.3) 50%, rgba(187, 247, 208, 0.3) 75%, transparent 100%)',
            'linear-gradient(90deg, transparent 0%, rgba(165, 243, 252, 0.3) 25%, rgba(187, 247, 208, 0.3) 50%, rgba(254, 240, 138, 0.3) 75%, transparent 100%)',
            'linear-gradient(90deg, transparent 0%, rgba(251, 207, 232, 0.3) 25%, rgba(196, 181, 253, 0.3) 50%, rgba(165, 243, 252, 0.3) 75%, transparent 100%)',
          ],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      
      {/* Sparkle decorations */}
      <div className="absolute top-2 left-2">
        <Sparkles className="h-4 w-4 text-pink-400/60" />
      </div>
      <div className="absolute top-2 right-2">
        <Sparkles className="h-4 w-4 text-purple-400/60" />
      </div>
      <div className="absolute bottom-2 left-2">
        <Sparkles className="h-4 w-4 text-cyan-400/60" />
      </div>
      <div className="absolute bottom-2 right-2">
        <Sparkles className="h-4 w-4 text-green-400/60" />
      </div>

      <div className="relative z-10">
        <div className="relative">
          <motion.div
            className="absolute -inset-1 rounded-full opacity-70"
            style={{
              background: 'linear-gradient(135deg, #fce7f3, #c4b5fd, #a5f3fc, #bbf7d0, #fef08a)',
            }}
            animate={{ 
              rotate: 360,
              scale: [1, 1.05, 1],
            }}
            transition={{ 
              rotate: { duration: 8, repeat: Infinity, ease: 'linear' },
              scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
            }}
          />
          <img
            src={avatarUrl}
            alt={staff.username}
            onError={(e) => { e.target.src = fallbackUrl; }}
            className="w-24 h-24 rounded-full object-cover border-2 border-purple-300 relative z-10 bg-muted"
            loading="lazy"
          />
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center z-20 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #fce7f3, #c4b5fd, #a5f3fc)',
            }}
          >
            <Sparkles className="h-4 w-4 text-purple-700" />
          </div>
        </div>
      </div>

      <div className="text-center relative z-10">
        <h3 className="font-display font-bold text-lg bg-gradient-to-r from-pink-400 via-purple-400 via-cyan-400 to-green-400 bg-clip-text text-transparent">
          {staff.username}
        </h3>
        <p className="text-sm font-semibold text-purple-400">
          {staff.role}
        </p>
        <p className="text-xs text-purple-500/80 dark:text-purple-300/80 mt-1">{staff.title}</p>
      </div>
    </motion.button>
  );
}

// Staff section component
function StaffSection({ title, icon: Icon, staff, roleColor }) {
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Get avatar URL for selected staff
  const getStaffAvatarUrl = (staffMember) => {
    if (!staffMember) return '';
    return staffMember.customAvatar
      ? staffMember.customAvatar
      : staffMember.placeholder
      ? staffConfig.unknownAvatar
      : `https://hyvatar.io/render/${staffMember.username}?size=256`;
  };

  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className={cn('p-2 rounded-lg', roleColor)}>
          <Icon className="h-5 w-5 text-primary-foreground" />
        </div>
        <h3 className="font-display text-xl font-bold text-foreground">{title}</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((member, index) => (
          <StaffCard
            key={`${member.username}-${index}`}
            staff={member}
            onSelect={setSelectedStaff}
            roleColor={roleColor}
          />
        ))}
      </div>

      {/* Staff Detail Modal */}
      <Modal
        isOpen={!!selectedStaff}
        onClose={() => setSelectedStaff(null)}
        title={selectedStaff?.username}
        description={selectedStaff?.role}
      >
        {selectedStaff && (
          <div className="p-6 flex flex-col items-center gap-6">
            <img
              src={getStaffAvatarUrl(selectedStaff)}
              alt={selectedStaff.username}
              onError={(e) => { e.target.src = 'https://hyvatar.io/render/NPC?size=256'; }}
              className="w-32 h-32 rounded-full object-cover border-4 border-border bg-muted"
            />
            <div className="text-center">
              <h3 className="font-display text-2xl font-bold text-foreground">
                {selectedStaff.username}
              </h3>
              <p className={cn('font-medium mt-1', roleColor.replace('bg-', 'text-'))}>
                {selectedStaff.role}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {selectedStaff.title}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 w-full">
              <p className="text-muted-foreground text-center">{selectedStaff.bio}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Special Thanks section with prisma styling - centered 3 top, 2 bottom layout
function SpecialThanksSection({ staff }) {
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Split staff into rows: first 3 on top, rest on bottom (centered)
  const topRow = staff.slice(0, 3);
  const bottomRow = staff.slice(3);

  return (
    <div className="mt-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex items-center justify-center gap-3 mb-8"
      >
        <div className="h-px flex-grow" style={{ background: 'linear-gradient(to right, transparent, #fce7f3, #c4b5fd, #a5f3fc, transparent)' }} />
        <div className="flex items-center gap-2 px-4">
          <Sparkles className="h-6 w-6 text-pink-400" />
          <h3 className="font-display text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 via-cyan-400 to-green-400 bg-clip-text text-transparent">
            Special Thanks
          </h3>
          <Sparkles className="h-6 w-6 text-cyan-400" />
        </div>
        <div className="h-px flex-grow" style={{ background: 'linear-gradient(to left, transparent, #a5f3fc, #bbf7d0, #fef08a, transparent)' }} />
      </motion.div>

      <div className="max-w-4xl mx-auto">
        {/* Top row - 3 cards centered */}
        <div className="flex justify-center gap-6 mb-6">
          {topRow.map((member, index) => (
            <div key={`${member.username}-${index}`} className="w-full max-w-[280px]">
              <SpecialThanksCard
                staff={member}
                onSelect={setSelectedStaff}
              />
            </div>
          ))}
        </div>
        
        {/* Bottom row - remaining cards centered */}
        {bottomRow.length > 0 && (
          <div className="flex justify-center gap-6">
            {bottomRow.map((member, index) => (
              <div key={`${member.username}-bottom-${index}`} className="w-full max-w-[280px]">
                <SpecialThanksCard
                  staff={member}
                  onSelect={setSelectedStaff}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Special Thanks Modal with prisma styling */}
      <Modal
        isOpen={!!selectedStaff}
        onClose={() => setSelectedStaff(null)}
        title={selectedStaff?.username}
        description={selectedStaff?.role}
      >
        {selectedStaff && (
          <div className="p-6 flex flex-col items-center gap-6">
            <div className="relative">
              <motion.div
                className="absolute -inset-2 rounded-full opacity-60 blur-sm"
                style={{
                  background: 'linear-gradient(135deg, #fce7f3, #c4b5fd, #a5f3fc, #bbf7d0)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              />
              <img
                src={`https://hyvatar.io/render/${selectedStaff.username}?size=256`}
                alt={selectedStaff.username}
                onError={(e) => { e.target.src = 'https://hyvatar.io/render/NPC?size=256'; }}
                className="w-32 h-32 rounded-full object-cover border-4 border-purple-400 relative z-10 bg-muted"
              />
            </div>
            <div className="text-center">
              <h3 className="font-display text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 via-cyan-400 to-green-400 bg-clip-text text-transparent">
                {selectedStaff.username}
              </h3>
              <p className="font-semibold text-purple-400 mt-1">
                {selectedStaff.role}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {selectedStaff.title}
              </p>
            </div>
            <div className="rounded-lg p-4 w-full" style={{ background: 'linear-gradient(135deg, rgba(251, 207, 232, 0.1), rgba(196, 181, 253, 0.1), rgba(165, 243, 252, 0.1))', border: '1px solid rgba(196, 181, 253, 0.3)' }}>
              <p className="text-muted-foreground text-center">{selectedStaff.bio}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Small supporter card with tier-specific gradient border
function SupporterCard({ supporter, onSelect }) {
  const avatarUrl = `https://hyvatar.io/render/${supporter.username}?size=128`;
  const fallbackUrl = 'https://hyvatar.io/render/NPC?size=128';
  const gradient = tierGradients[supporter.tier] || tierGradients.Copper;
  const borderColor = tierBorderColors[supporter.tier] || tierBorderColors.Copper;
  const textColor = tierTextColors[supporter.tier] || tierTextColors.Copper;

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onSelect(supporter)}
      className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-card/30 transition-all w-16"
    >
      <div className="relative w-12 h-12">
        {/* Gradient border ring */}
        <div className={cn(
          'absolute -inset-1 rounded-full bg-gradient-to-br opacity-80',
          gradient
        )} />
        <img
          src={avatarUrl}
          alt={supporter.username}
          onError={(e) => { e.target.src = fallbackUrl; }}
          className={cn(
            'w-12 h-12 rounded-full object-cover relative z-10 border-2 bg-muted',
            borderColor
          )}
          loading="lazy"
        />
      </div>
      <span className={cn('text-[10px] font-medium text-center leading-tight truncate w-full', textColor)}>
        {supporter.username}
      </span>
    </motion.button>
  );
}

// Thanks for Supporter section - single flowing grid sorted by tier
function SupportersSection({ supporters }) {
  const [selectedSupporter, setSelectedSupporter] = useState(null);

  // Order: Mithril, Adamantite, Cobalt, Thorium, Gold, Silver, Iron, Copper
  const tierOrder = ['Mithril', 'Adamantite', 'Cobalt', 'Thorium', 'Gold', 'Silver', 'Iron', 'Copper'];
  
  // Flatten all supporters into a single sorted array
  const allSupporters = tierOrder.flatMap(tierName => {
    const tierSupporters = supporters[tierName.toLowerCase()] || [];
    return tierSupporters;
  });

  if (allSupporters.length === 0) return null;

  return (
    <div className="mt-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex items-center justify-center gap-3 mb-8"
      >
        <div className="h-px flex-grow bg-gradient-to-r from-transparent via-muted-foreground/30 to-transparent" />
        <div className="flex items-center gap-2 px-4">
          <Heart className="h-5 w-5 text-red-400" fill="currentColor" />
          <h3 className="font-display text-xl font-bold text-foreground">
            Thanks for Supporter
          </h3>
          <Heart className="h-5 w-5 text-red-400" fill="currentColor" />
        </div>
        <div className="h-px flex-grow bg-gradient-to-l from-transparent via-muted-foreground/30 to-transparent" />
      </motion.div>

      <div className="max-w-3xl mx-auto bg-card/20 backdrop-blur-sm rounded-xl p-6 border border-border/50">
        <div className="flex flex-wrap justify-center gap-3">
          {allSupporters.map((supporter, index) => (
            <SupporterCard
              key={`${supporter.username}-${index}`}
              supporter={supporter}
              onSelect={setSelectedSupporter}
            />
          ))}
        </div>
      </div>

      {/* Supporter Detail Modal */}
      <Modal
        isOpen={!!selectedSupporter}
        onClose={() => setSelectedSupporter(null)}
        title={selectedSupporter?.username}
        description={`${selectedSupporter?.tier} Supporter`}
      >
        {selectedSupporter && (
          <div className="p-6 flex flex-col items-center gap-6">
            <div className="relative">
              <motion.div
                className={cn(
                  'absolute -inset-2 rounded-full opacity-70 blur-sm bg-gradient-to-br',
                  tierGradients[selectedSupporter.tier]
                )}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              />
              <img
                src={`https://hyvatar.io/render/${selectedSupporter.username}?size=256`}
                alt={selectedSupporter.username}
                onError={(e) => { e.target.src = 'https://hyvatar.io/render/NPC?size=256'; }}
                className={cn(
                  'w-32 h-32 rounded-full object-cover border-4 relative z-10 bg-muted',
                  tierBorderColors[selectedSupporter.tier]
                )}
              />
            </div>
            <div className="text-center">
              <h3 className={cn(
                'font-display text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent',
                tierGradients[selectedSupporter.tier]
              )}>
                {selectedSupporter.username}
              </h3>
              <p className={cn('font-semibold mt-1', tierTextColors[selectedSupporter.tier])}>
                {selectedSupporter.tier} Supporter
              </p>
            </div>
            <div className="bg-muted/30 border border-border/50 rounded-lg p-4 w-full">
              <p className="text-muted-foreground text-center">
                Terima kasih atas dukungan Anda sebagai {selectedSupporter.tier} Supporter untuk server Hytale Indonesia! 💖
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function StaffPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <Section>
          <SectionHeader
            subtitle="Our Team"
            title="Staff Team"
            description="Meet the dedicated team behind Hytale Indonesia who work hard to make your gaming experience amazing."
          />

          {/* Owners */}
          <StaffSection
            title="Owners"
            icon={UserCheck}
            staff={staffConfig.owners}
            roleColor="bg-primary"
          />

          {/* Developers */}
          <StaffSection
            title="Developers"
            icon={User}
            staff={staffConfig.developers}
            roleColor="bg-purple-500"
          />

          {/* Admins */}
          <StaffSection
            title="Admin"
            icon={User}
            staff={staffConfig.admins}
            roleColor="bg-red-500"
          />

          {/* Moderators */}
          <StaffSection
            title="Moderator"
            icon={User}
            staff={staffConfig.moderators}
            roleColor="bg-yellow-500"
          />

          {/* Helpers */}
          <StaffSection
            title="Helper"
            icon={User}
            staff={staffConfig.helpers}
            roleColor="bg-green-500"
          />

          {/* Special Thanks */}
          <SpecialThanksSection staff={staffConfig.specialThanks} />

          {/* Thanks for Supporter */}
          <SupportersSection supporters={staffConfig.supporters} />
        </Section>
      </main>
      <Footer />
    </div>
  );
}
